import type { ApiScan } from '@kaiizen/kai-pro-api';
import { ScanGroupOptimized, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';

interface ScanWithParent {
  id: string;
  parentScanId?: string | null;
}

/**
 * Finds the leaf scan from an array of scans.
 *
 * A "leaf" scan is a scan that is not the parent of any other scan,
 * representing the most recent scan in the lineage.
 *
 * Child scans inherit their parent's createdAt date so it cannot be used
 * and updatedAt seems to return ambiguous results
 *
 * @param scans Array of scans with id and parentScanId properties
 * @returns The first leaf scan found, or null if no scans provided
 */
export const findLeafScan = <T extends ScanWithParent>(scans: T[] | undefined): T | null => {
  if (!scans || scans.length === 0) return null;
  const parentScanIds = new Set(scans.map(s => s.parentScanId).filter(Boolean));
  return scans.find(s => !parentScanIds.has(s.id)) ?? null;
};

export const getFilteredGroups = (groups?: ScanGroupOptimized[], filterRegex?: RegExp) =>
  groups
    ? filterRegex ? groups.filter(group => filterRegex.test(group.optimized.searchText)) : groups
    : [];

export const getFilteredTasks = (tasks: ScanTaskOptimized[], filterRegex?: RegExp) =>
  filterRegex ? tasks.filter(task => filterRegex.test(task.optimized.searchText)) : tasks;

export const getCategorizedGroups = (groups: ScanGroupOptimized[]) => {
  const categorizedGroups: Record<string, ScanGroupOptimized[]> = {};

  groups?.forEach((group, index) => {
    const { category = 'other' } = group.template;
    if (!categorizedGroups[category]) categorizedGroups[category] = [];
    categorizedGroups[category].push(group);
  });

  return categorizedGroups;
};

/**
 * Returns the approved parent change order scan id for the current scan, if one exists.
 *
 * When a change order is approved the backend immediately auto-creates a child progress
 * scan. The child becomes the active/latest scan, but the approved parent change order
 * document still needs to stay reachable from that new scan's UI.
 */
export const getApprovedParentChangeOrderScanId = (
  scan?: Pick<ApiScan, 'parentScanId'> | null,
  parentScan?: Pick<ApiScan, 'id' | 'scanLifecycleStatus' | 'scanType'> | null,
) => {
  if (!scan?.parentScanId || parentScan?.id !== scan.parentScanId) return undefined;
  return parentScan.scanType === 'changeRequest' && parentScan.scanLifecycleStatus === 'approved'
    ? parentScan.id
    : undefined;
};
